import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { IssuePlannerView } from './types';

export interface IssuePlannerToolbarProps {
  view: IssuePlannerView;
  onViewChange: (next: IssuePlannerView) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
  searchLabel?: string;
  searchPlaceholder?: string;
  isSearchDisabled?: boolean;
  selectedKit?: string;
  onKitChange?: (kit: string) => void;
  availableKits?: string[];
}

export const IssuePlannerToolbar = ({
  view,
  onViewChange,
  search,
  onSearchChange,
  searchLabel,
  searchPlaceholder,
  isSearchDisabled,
  selectedKit,
  onKitChange,
  availableKits,
}: IssuePlannerToolbarProps) => {
  const { label: ieKitLabel, labelPlural: ieKitLabelPlural } = useIeKitLabels('front_window');
  const hasKitFilter = availableKits && availableKits.length > 0;

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            View:
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={view}
            exclusive
            onChange={(_event, nextValue: IssuePlannerView | null) => {
              if (nextValue) {
                onViewChange(nextValue);
              }
            }}
            aria-label="Issue planner view"
          >
            <ToggleButton value="missing">Shortages</ToggleButton>
            <ToggleButton value="all">All {ieKitLabel} items</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
          {hasKitFilter && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="kit-filter-label">{ieKitLabel}</InputLabel>
              <Select
                labelId="kit-filter-label"
                id="kit-filter"
                value={selectedKit ?? 'all'}
                label={ieKitLabel}
                onChange={(event) => {
                  onKitChange?.(event.target.value);
                }}
              >
                <MenuItem value="all">All {ieKitLabelPlural}</MenuItem>
                {availableKits.map((kitName) => (
                  <MenuItem key={kitName} value={kitName}>
                    {kitName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            size="small"
            label={searchLabel}
            placeholder={searchPlaceholder}
            value={search ?? ''}
            onChange={(event) => {
              onSearchChange?.(event.target.value);
            }}
            sx={{ width: { xs: '100%', md: 320 } }}
            disabled={isSearchDisabled ?? typeof onSearchChange !== 'function'}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
